import React, { useMemo, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { Prefix } from '../../../../services/prefix'
import { useFetch } from '../../../../components/hooks/useFetch'
import EnergyPhasesComponent from './EnergyPhasesComponent'
import './chartEnergySkeleton.css'

const ChartEnergyComponent = ({ unit, height = 350 }) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [activeTab, setActiveTab] = useState('geral');
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [selectedMonth, setSelectedMonth] = useState(currentMonth)
  const [filters, setFilters] = useState({ year: currentYear, month: currentMonth })

  const chartDetail = `${Prefix.replace(/[\\"]/g, '')}/sismogran/map/units/${unit.id}/energy/${filters.year}/${filters.month}`
  const { data, loading } = useFetch(chartDetail)

  const handleFilter = () => {
    setFilters({ year: selectedYear, month: selectedMonth })
  }

  const chartData = useMemo(() => {
    const daysObj = data?.models?.days
    if (!daysObj) return null

    const sortedKeys = Object.keys(daysObj).sort((a, b) => Number(a) - Number(b))

    const days = []
    const kwhValues = []
    const moneyValues = []
    const accumulatedValues = []

    sortedKeys.forEach(key => {
      const dayData = daysObj[key]
      if (!dayData) return
      days.push(key)
      kwhValues.push(dayData.kwh || 0)
      moneyValues.push(dayData.value || 0)
      accumulatedValues.push(dayData.accumulated_value || 0)
    })

    return { days, kwhValues, moneyValues, accumulatedValues }
  }, [data])

  const overviewKwh = data?.models?.kwh
  const overviewMoney = data?.models?.value

  const years = [currentYear - 2, currentYear - 1, currentYear]
  const months = [
    { v: 1, n: 'Janeiro' }, { v: 2, n: 'Fevereiro' }, { v: 3, n: 'Março' },
    { v: 4, n: 'Abril' }, { v: 5, n: 'Maio' }, { v: 6, n: 'Junho' },
    { v: 7, n: 'Julho' }, { v: 8, n: 'Agosto' }, { v: 9, n: 'Setembro' },
    { v: 10, n: 'Outubro' }, { v: 11, n: 'Novembro' }, { v: 12, n: 'Dezembro' }
  ]

  if (loading || !chartData || !overviewKwh || !overviewMoney) {
    return (
      <div className="chart-skeleton-wrapper">
        <div className="skeleton-header">
          <div className="skeleton-title" />
          <div className="skeleton-metrics">
            <div className="skeleton-metric" /><div className="skeleton-metric" />
            <div className="skeleton-metric" /><div className="skeleton-metric" />
          </div>
        </div>
        <div className="skeleton-chart" style={{ height }} />
      </div>
    )
  }

  const generalChartState = {
    series: [
      { name: 'Consumo (kWh)', type: 'column', data: chartData.kwhValues },
      { name: 'Valor (R$)', type: 'column', data: chartData.moneyValues },
      { name: 'Acumulado (R$)', type: 'line', data: chartData.accumulatedValues },
    ],
    options: {
      chart: {
        height: height,
        type: 'line',
        toolbar: { show: false },
      },
      stroke: {
        width: [0, 0, 3],
        curve: 'smooth'
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '70%',
        }
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: chartData.days,
      },
      yaxis: [
        {
          seriesName: 'Consumo (kWh)',
          title: { text: 'Consumo (kWh)' },
          labels: { formatter: (val) => val.toFixed(1) }
        },
        {
          seriesName: 'Valor (R$)',
          opposite: true,
          title: { text: 'Valor (R$)' },
          labels: { formatter: (val) => `R$ ${val.toFixed(2)}` }
        },
        {
          seriesName: 'Valor (R$)',
          show: false
        }
      ],
      colors: ['#4f46e5', '#10b981', '#f59e0b'],
      legend: {
        position: 'top',
        showForSingleSeries: true,
        onItemClick: {
          toggleDataSeries: true
        },
        onItemHover: {
          highlightDataSeries: true
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val, { seriesIndex }) => {
            if (seriesIndex === 0) return `${val.toFixed(2)} kWh`
            return `R$ ${val.toFixed(2)}`
          }
        }
      }
    },
  }

  return (
    <div className="chart-container">
      <div className="chart-header">
        <div className="header-top-row">
          <div className="chart-title">Energia</div>

          <div className="tabs-container">
            <button
              onClick={() => setActiveTab('geral')}
              className={`filter-button ${activeTab === 'geral' ? 'active' : ''}`}
            >
              Geral
            </button>
            <button
              onClick={() => setActiveTab('fases')}
              className={`filter-button ${activeTab === 'fases' ? 'active' : ''}`}
            >
              Fases
            </button>
          </div>

          <div className="filter-group">
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))} className="filter-select">
              {months.map(m => <option key={m.v} value={m.v}>{m.n}</option>)}
            </select>
            <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} className="filter-select">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button className="filter-button" onClick={handleFilter}>Filtrar</button>
          </div>
        </div>

        <div className="chart-metrics">
          <div className="metric-card">
            <span>Total</span>
            <strong>{overviewKwh.total.toFixed(2)} kWh</strong>
            <small>R$ {overviewMoney.total.toLocaleString('pt-br', { minimumFractionDigits: 2 })}</small>
          </div>
          <div className="metric-card">
            <span>Média</span>
            <strong>{overviewKwh.avg.toFixed(2)} kWh</strong>
            <small>R$ {overviewMoney.avg.toLocaleString('pt-br', { minimumFractionDigits: 2 })}</small>
          </div>
          <div className="metric-card">
            <span>Mínimo</span>
            <strong>{overviewKwh.min.toFixed(2)} kWh</strong>
            <small>R$ {overviewMoney.min.toLocaleString('pt-br', { minimumFractionDigits: 2 })}</small>
          </div>
          <div className="metric-card">
            <span>Máximo</span>
            <strong>{overviewKwh.max.toFixed(2)} kWh</strong>
            <small>R$ {overviewMoney.max.toLocaleString('pt-br', { minimumFractionDigits: 2 })}</small>
          </div>
        </div>
      </div>

      <div className="chart-content">
        {activeTab === 'geral' ? (
          <ReactApexChart
            options={{
              ...generalChartState.options,
              chart: {
                ...generalChartState.options.chart,
                events: {
                  mounted: (chart) => {
                    chart.hideSeries('Acumulado (R$)')
                  }
                }
              }
            }}
            series={generalChartState.series}
            type="line"
            height={height}
            width="100%"
          />
        ) : (
          <EnergyPhasesComponent sensors={data?.models?.sensors} height={height} />
        )}
      </div>
    </div>
  )
}

export default ChartEnergyComponent